import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { createAdminTeamMember, updateAdminTeamMember } from '../../../shared/api/team.services';
import { getLocalizedValue } from '../../../shared/lib/localization';
import { slugify as sharedSlugify } from '../../../shared/lib/slugify';
import toast from '../../../shared/lib/toast';

// Адреса учасника — латиницею через спільний транслітератор, той самий, що
// у програмах і відгуках. До цього слаги виходили або кирилицею, або зовсім
// сміттям («-i-», «-») — залежно від того, яка версія генератора діяла.
const slugify = (name) => sharedSlugify(name);

const generateIdFromName = (name) => {
    if (!name) return '';
    return `team-member-${slugify(name)}`;
};

const generateSlugFromName = (name) => {
    if (!name) return '';
    return slugify(name);
};

const validationSchema = Yup.object({
    fullName: Yup.object({
        ua: Yup.string().required('Введіть імʼя'),
        en: Yup.string(),
    }),
    role: Yup.object({
        ua: Yup.string().required('Введіть посаду'),
        en: Yup.string(),
    }),
    expertise: Yup.object({
        ua: Yup.string(),
        en: Yup.string(),
    }),
    bio: Yup.object({
        ua: Yup.string(),
        en: Yup.string(),
    }),
    photoUrl: Yup.string(),
    sortOrder: Yup.number().min(0, 'Порядок має бути невід\'ємним числом'),
    isFeatured: Yup.boolean(),
    isActive: Yup.boolean(),
});

const buildInitialValues = (initialData) => {
    if (!initialData) {
        return {
            fullName: { ua: '', en: '' },
            role: { ua: '', en: '' },
            expertise: { ua: '', en: '' },
            bio: { ua: '', en: '' },
            photoUrl: '',
            sortOrder: 0,
            isFeatured: false,
            isActive: true,
        };
    }

    return {
        fullName: {
            ua: getLocalizedValue(initialData?.fullName || initialData?.name, 'ua') || '',
            en: getLocalizedValue(initialData?.fullName || initialData?.name, 'en') || '',
        },
        role: {
            ua: getLocalizedValue(initialData?.role, 'ua') || '',
            en: getLocalizedValue(initialData?.role, 'en') || '',
        },
        expertise: {
            ua: getLocalizedValue(initialData?.expertise, 'ua') || '',
            en: getLocalizedValue(initialData?.expertise, 'en') || '',
        },
        bio: {
            ua: getLocalizedValue(initialData?.bio || initialData?.description, 'ua') || '',
            en: getLocalizedValue(initialData?.bio || initialData?.description, 'en') || '',
        },
        photoUrl: initialData?.photoUrl || initialData?.photo || initialData?.imageURL || '',
        sortOrder: initialData?.sortOrder || 0,
        isFeatured: initialData?.isFeatured || false,
        isActive: initialData?.isActive !== false,
    };
};

export const useTeamForm = (type, initialData) => {
    const navigate = useNavigate();

    return useFormik({
        enableReinitialize: true,
        initialValues: buildInitialValues(initialData),
        validationSchema,
        onSubmit: async (values) => {
            try {
                // При создании генерируем id/slug из имени, при редактировании
                // сохраняем существующие, чтобы не ломать ссылки и не плодить
                // дубли slug (генератор не поддерживает кириллицу)
                const isCreateMode = type === 'create';
                const generatedId = !isCreateMode && initialData?.id
                    ? initialData.id
                    : generateIdFromName(values.fullName.ua);
                const generatedSlug = !isCreateMode && initialData?.slug
                    ? initialData.slug
                    : generateSlugFromName(values.fullName.ua);

                const payload = {
                    id: generatedId,
                    slug: generatedSlug,
                    fullName: values.fullName,
                    role: values.role,
                    expertise: values.expertise,
                    bio: values.bio,
                    photoUrl: values.photoUrl,
                    sortOrder: values.sortOrder,
                    isFeatured: values.isFeatured,
                    isActive: values.isActive,
                };

                if (type === 'create') {
                    const response = await createAdminTeamMember(payload);
                    if (response?.error) throw new Error(response.message || 'Помилка при створенні');
                    toast.success('Учасника успішно створено!');
                } else {
                    const memberId = initialData?._id || initialData?.id;
                    if (!memberId) throw new Error('ID учасника не знайдено');
                    const response = await updateAdminTeamMember(memberId, payload);
                    if (response?.error) throw new Error(response.message || 'Помилка при оновленні');
                    toast.success('Учасника успішно оновлено!');
                }

                navigate('/team');
            } catch (error) {
                console.error('Помилка при збереженні:', error);
                toast.error(error.message || 'Сталася помилка. Спробуйте ще раз.');
            }
        },
    });
};
